import { useEffect, useState } from "react";
import type { EngagementInsight, EngagementParticipant } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useConnectionContext } from "@/lib/connectionContext";
import { ChartLegend, HourlyChart, type HourlySeries } from "@/components/charts/HourlyChart";

type Loadable<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "error"; message: string };

/**
 * Insights → Engagement. "Who's talking back?"
 * Direction mix in the summary + chart, top participants in two leaderboards.
 */
export function EngagementPage() {
  const ctx = useConnectionContext();
  const scope = ctx.allSelected ? undefined : ctx.selectedIds;
  const scopeKey = scope ? scope.join(",") : "all";

  const [insight, setInsight] = useState<Loadable<EngagementInsight>>({
    status: "loading",
  });

  useEffect(() => {
    if (ctx.status !== "ready") return;
    let cancelled = false;
    setInsight({ status: "loading" });
    void api.insights
      .engagement(scope)
      .then((data) => {
        if (!cancelled) setInsight({ status: "ready", data });
      })
      .catch((err) => {
        if (!cancelled) {
          setInsight({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load",
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey]);

  return (
    <div className="space-y-8">
      <header>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Insights
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Engagement
        </h1>
        <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
          {scopeLabel(ctx)} · Last 24 hours.
        </p>
      </header>

      {insight.status === "loading" && <LoadingState />}
      {insight.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {insight.message}
        </p>
      )}
      {insight.status === "ready" && insight.data.summary.total === 0 && <EmptyState />}
      {insight.status === "ready" && insight.data.summary.total > 0 && (
        <>
          <SummaryRow data={insight.data} />
          <DirectionChart data={insight.data} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ParticipantsTable
              title="Top inbound senders"
              hint="Who's reaching out · `from` on inbound messages."
              rows={insight.data.topInboundFrom}
              shareLabel="of inbound"
            />
            <ParticipantsTable
              title="Top outbound recipients"
              hint="Where we're sending · `to` on outbound messages."
              rows={insight.data.topOutboundTo}
              shareLabel="of outbound"
            />
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function SummaryRow({ data }: { data: EngagementInsight }) {
  const s = data.summary;
  const cards: Array<{ label: string; value: string; hint: string }> = [
    {
      label: "Inbound · 24h",
      value: formatInt(s.inbound),
      hint: shareLabel(s.inbound, s.total),
    },
    {
      label: "Outbound · 24h",
      value: formatInt(s.outbound),
      hint: shareLabel(s.outbound, s.total),
    },
    {
      label: "Conversation rate",
      value:
        s.conversationRate === null
          ? "—"
          : s.conversationRate >= 100
            ? `${s.conversationRate.toFixed(0)}×`
            : s.conversationRate >= 1
              ? `${s.conversationRate.toFixed(2)}×`
              : s.conversationRate.toFixed(2),
      hint: "inbound ÷ outbound",
    },
    {
      label: "Unique participants",
      value: formatInt(s.uniqueParticipants),
      hint: "Distinct inbound senders",
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none p-4 dark:ring-1 dark:ring-inset dark:ring-white/[0.02]"
        >
          <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            {c.label}
          </div>
          <div className="mt-1.5 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            {c.value}
          </div>
          <div className="mt-1 text-[11.5px] text-zinc-500">{c.hint}</div>
        </div>
      ))}
    </div>
  );
}

function shareLabel(part: number, total: number): string {
  if (total <= 0) return "—";
  return `${Math.round((part / total) * 100)}% of total`;
}

// ---------------------------------------------------------------------------

function DirectionChart({ data }: { data: EngagementInsight }) {
  const series: HourlySeries[] = [
    {
      label: "Inbound",
      fill: "rgb(167 139 250 / 0.85)",
      values: data.hourly.map((b) => b.inbound),
    },
    {
      label: "Outbound",
      fill: "rgb(56 189 248 / 0.85)",
      values: data.hourly.map((b) => b.outbound),
    },
  ];
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none p-5 dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <header className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100">
            Hourly direction mix · last 24h
          </h2>
          <p className="text-[11.5px] text-zinc-500">Stacked: inbound + outbound message volume.</p>
        </div>
        <ChartLegend series={series} />
      </header>
      <HourlyChart
        hours={data.hourly.map((b) => b.hour)}
        series={series}
        ariaLabel="Hourly inbound vs outbound message volume"
      />
    </section>
  );
}

// ---------------------------------------------------------------------------

function ParticipantsTable({
  title,
  hint,
  rows,
  shareLabel,
}: {
  title: string;
  hint: string;
  rows: EngagementParticipant[];
  shareLabel: string;
}) {
  return (
    <section>
      <header className="mb-2">
        <h2 className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
        <p className="text-[11.5px] text-zinc-500">{hint}</p>
      </header>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-6 text-center text-[13px] text-zinc-600 dark:text-zinc-400">
          No traffic in the last 24 hours.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider dark:bg-zinc-900/60 text-zinc-500">
              <tr>
                <th className="px-4 py-2.5">Number</th>
                <th className="px-4 py-2.5 text-right">Count</th>
                <th className="px-4 py-2.5">Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
              {rows.map((r) => (
                <tr
                  key={r.number}
                  className="transition hover:bg-zinc-50 dark:hover:bg-zinc-900/60"
                >
                  <td className="px-4 py-3 align-middle font-mono text-[12.5px] text-zinc-900 dark:text-zinc-100">
                    {r.number}
                  </td>
                  <td className="px-4 py-3 align-middle text-right font-mono text-[12px] text-zinc-700 dark:text-zinc-300">
                    {formatInt(r.count)}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-1.5 w-16 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
                        <span
                          className="block h-full bg-violet-500 dark:bg-violet-400/70"
                          style={{ width: `${Math.min(100, Math.round(r.share * 100))}%` }}
                        />
                      </span>
                      <span className="font-mono text-[11.5px] text-zinc-600 dark:text-zinc-400">
                        {Math.round(r.share * 100)}% {shareLabel}
                      </span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-[88px] animate-pulse rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none"
          />
        ))}
      </div>
      <div className="h-[200px] animate-pulse rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-10 text-center">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
        No engagement signal in the last 24 hours
      </h2>
      <p className="mx-auto mt-1.5 max-w-md text-[13px] text-zinc-600 dark:text-zinc-400">
        Direction mix and participant leaderboards appear here once message traffic flows through
        your connected Twilio accounts.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function scopeLabel(ctx: ReturnType<typeof useConnectionContext>): string {
  if (ctx.connections.length === 0) return "No accounts connected yet";
  if (ctx.allSelected)
    return `Across all ${ctx.connections.length} ${ctx.connections.length === 1 ? "account" : "accounts"}`;
  if (ctx.selectedIds.length === 1) {
    const c = ctx.connections.find((x) => x.id === ctx.selectedIds[0]);
    return `Scoped to ${c?.friendlyName ?? c?.accountSid ?? "1 account"}`;
  }
  return `Scoped to ${ctx.selectedIds.length} of ${ctx.connections.length} accounts`;
}

function formatInt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}
